import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { getBankAccountSchema } from '~domains/partner-application/entities/application';
import {
  bankAccountSchema,
  type BankAccountFormValues,
} from '~domains/partner-application/entities/application';
import { uploadPartnerApplicationDocument } from '~domains/partner-application/entities/application/api';
import { useDisbursementProviders } from '~domains/partner-application/entities/disbursement-providers';
import { SelectDisbursementProvider } from '~domains/partner-application/features/select-disbursement-provider';
import {
  PARTNER_APPLICATION_TOTAL_STEPS,
  STEP_INDEX_BUSINESS,
} from '~domains/partner-application/shared/constants';
import { getApplicationEventProperties } from '~domains/partner-application/shared/lib';

import { ampli } from '~shared/analytics';
import { useDynamicMask } from '~shared/lib/use-dynamic-mask';
import { FileUpload, type FileUploadRef } from '~shared/ui/file-upload/rhf';
import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { TextField } from '@breezeventures/ui-kit/text-field/rhf';
import { toast } from '@breezeventures/ui-kit/toast';
import { Typography } from '@breezeventures/ui-kit/typography';
import { InputMasked } from '@breezeventures/web-ph-components/input-masked';
import { zodResolver } from '@hookform/resolvers/zod';

export const proofOfAccountFileOptions = {
  allowedExtensions: ['pdf', 'jpg', 'jpeg', 'png'],
  maxSize: 10 * 1024 * 1024, // 10MB
  multiple: true,
};

type BankAccountProps = {
  applicationId?: string;
  initialValues?: BankAccountFormValues;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (values: BankAccountFormValues) => Promise<void>;
};

export const BankAccount = ({
  applicationId,
  initialValues,
  onBack,
  onClose,
  onSubmit,
}: BankAccountProps) => {
  const proofsOfAccountFileUploadRef = useRef<FileUploadRef>(null);

  const { data: providers } = useDisbursementProviders();
  const [schema, setSchema] = useState(bankAccountSchema);

  const methods = useForm<BankAccountFormValues>({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: initialValues ?? {
      providerId: '',
      beneficiaryName: '',
      accountNumber: '',
      proofsOfAccount: [],
    },
  });

  const { control, setValue, watch } = methods;

  const providerId = watch('providerId');
  const accountNumber = watch('accountNumber');

  const currentProvider = useMemo(() => {
    return providers?.find((p) => p.id === providerId) ?? null;
  }, [providers, providerId]);

  const lengthVariants = useMemo(
    () =>
      currentProvider?.fields?.validationParameters?.map((p) => p.length) ?? [],
    [currentProvider]
  );

  const { maskitoMask, placeholder, regexpPattern } = useDynamicMask({
    lengthVariants,
    value: accountNumber,
  });

  useEffect(() => {
    if (initialValues) {
      methods.trigger();
    }
  }, [initialValues, methods]);

  useEffect(() => {
    if (!providerId) {
      setValue('accountNumber', '');
    }
  }, [providerId, setValue]);

  useEffect(() => {
    setSchema(getBankAccountSchema(new RegExp(regexpPattern)));
  }, [regexpPattern]);

  const handleSubmit = async (values: BankAccountFormValues) => {
    if (proofsOfAccountFileUploadRef.current?.checkIsUploading()) {
      toast('Still uploading, boss!', { type: 'info' });
      return;
    }
    await onSubmit(values);
  };

  return (
    <form
      onSubmit={methods.handleSubmit(handleSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<ScreenHeader onBack={onBack} onClose={onClose} />}
        footer={
          <FooterWithProgressBar
            currentStepIndex={STEP_INDEX_BUSINESS}
            totalSteps={PARTNER_APPLICATION_TOTAL_STEPS}
            isLoading={methods.formState.isSubmitting}
          />
        }
      >
        <div className="flex flex-col gap-3 py-6">
          <Typography.Title size="s">
            Fill in info on bank account
          </Typography.Title>
          <Typography.Text size="m" color="secondary">
            Payments from Skyro will be sent to this account.
          </Typography.Text>
        </div>

        <div className="flex flex-col gap-3">
          <SelectDisbursementProvider
            name="providerId"
            control={control}
            onChange={() => setValue('accountNumber', '')}
          />

          <Controller
            name="beneficiaryName"
            control={control}
            render={(fieldProps) => (
              <TextField
                {...fieldProps}
                label="Bank Account Name"
                labelPosition="inner"
              />
            )}
          />

          {currentProvider && (
            <Controller
              name="accountNumber"
              control={control}
              render={(fieldProps) => (
                <InputMasked
                  {...fieldProps}
                  label="Bank Account Number"
                  labelPosition="inner"
                  maskitoOptions={{ mask: maskitoMask }}
                  placeholder={placeholder}
                />
              )}
            />
          )}

          <Controller
            name="proofsOfAccount"
            control={control}
            render={(props) => (
              <FileUpload
                ref={proofsOfAccountFileUploadRef}
                {...props}
                title="Proof of Bank Account"
                description="Examples: passbook copy, screenshot of bank app, copy of statement of account"
                options={proofOfAccountFileOptions}
                onUpload={async (req, opts) => {
                  const result = await uploadPartnerApplicationDocument(
                    req,
                    opts
                  );
                  ampli.mpPaBankingProofOfAccountUploaded(
                    getApplicationEventProperties(applicationId)
                  );
                  return result;
                }}
              />
            )}
          />
        </div>
      </Screen>
    </form>
  );
};
